import React, { useCallback, useEffect, useState } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Alert, AlertDescription } from '../ui/alert';
import { Spinner } from '../ui/spinner';
import { AlertCircle, Copy, Check, Trash2 } from 'lucide-react';
import crmTokenAPI from '../../services/crmTokenService';

// Where a CRM user mints the token their Customer.io workspace authenticates with.
//
// A token minted here belongs to one person: the workspace's calls to the localization endpoint
// name them, it expires on its own, and it can be revoked for one workspace without disturbing
// anyone else's.
//
// Exported without the page heading because a personal token is an account setting, not a
// destination: it lives on the profile page, next to the avatar.
export const CrmTokensPanel = () => {
  const [tokens, setTokens] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [label, setLabel] = useState('');
  const [creating, setCreating] = useState(false);
  // The plaintext of a token just minted. Held in memory only, and only until the page is left:
  // the server cannot return it a second time.
  const [fresh, setFresh] = useState(null);
  const [copied, setCopied] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await crmTokenAPI.list();
      setTokens(data.tokens || []);
      setError('');
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load tokens');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const create = async () => {
    if (creating) return;
    setCreating(true);
    setError('');
    try {
      const { data } = await crmTokenAPI.create({ label: label.trim() });
      setFresh(data.token);
      setCopied(false);
      setLabel('');
      await load();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to create a token');
    } finally {
      setCreating(false);
    }
  };

  const revoke = async (id) => {
    // Irreversible and immediate — the workspace holding it stops working on its next call — so it
    // asks first. There is no undo to offer afterwards.
    if (!window.confirm('Revoke this token? A workspace using it stops working immediately.')) return;
    try {
      await crmTokenAPI.revoke(id);
      await load();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to revoke the token');
    }
  };

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(fresh);
      setCopied(true);
    } catch {
      // Clipboard access can be refused; the token is on screen and selectable either way.
      setCopied(false);
    }
  };

  const formatDate = (value) => (value ? new Date(value).toLocaleDateString() : '—');

  return (
    <div className="space-y-6">
      {fresh && (
        <Card className="border-cta/40">
          <CardHeader>
            <CardTitle className="text-base">Your new token</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <p className="text-sm text-ink-dim font-medium">
              Copy it now and paste it into the Customer.io workspace (the localization endpoint's
              bearer token). This is the only time it is shown — the server keeps a salted hash, so
              it cannot be read back. If you lose it, revoke it and mint another.
            </p>
            <div className="flex items-center gap-2">
              <code className="flex-1 rounded-lg border border-white/10 bg-surface-2 px-3 py-2 font-mono text-sm text-ink break-all">{fresh}</code>
              <Button variant="outline" size="sm" onClick={copy} aria-label="Copy token">
                {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Create a token</CardTitle>
        </CardHeader>
        <CardContent className="flex items-end gap-3">
          <div className="flex-1">
            <label htmlFor="crm-token-label" className="text-sm text-ink-dim font-medium">
              Label — which workspace is this for?
            </label>
            <Input
              id="crm-token-label"
              value={label}
              placeholder="production workspace"
              maxLength={100}
              onChange={(e) => setLabel(e.target.value)}
            />
          </div>
          <Button onClick={create} disabled={creating}>
            {creating ? 'Creating…' : 'Create'}
          </Button>
        </CardContent>
      </Card>

      {error && (
        <Alert variant="error">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Your tokens</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <Spinner />
          ) : tokens.length === 0 ? (
            <p className="text-sm text-ink-dim font-medium">
              No tokens yet. Create one above, then paste it into the Customer.io workspace.
            </p>
          ) : (
            <ul className="divide-y divide-white/10">
              {tokens.map((token) => (
                <li key={token.id} className="flex items-center justify-between py-3 gap-4">
                  <div className="min-w-0">
                    <div className="text-sm font-bold text-ink truncate">
                      {token.label || 'Unlabelled'}{' '}
                      <code className="font-mono text-xs text-ink-mute">{token.keyId}</code>
                    </div>
                    <div className="text-xs text-ink-mute font-medium">
                      Created {formatDate(token.createdAt)} · Expires {formatDate(token.expiresAt)} ·
                      Last used {formatDate(token.lastUsedAt)}
                    </div>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => revoke(token.id)}
                    aria-label={`Revoke ${token.label || 'token'}`}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default CrmTokensPanel;
